import type { Action } from '../action'
import { ResourceType } from '../resource-type'

/**
 * The admin hierarchy — tenants, zones, apps, scenarios — and the users in it.
 *
 * `create` is granted on the parent: creating a zone is an action on its
 * tenant. There is no "delete unowned" action: deleting someone else's
 * scenario is `admin:scenario:delete` *without* an ownership condition
 */
export const ADMIN_ACTIONS = {
  'admin:tenant:read': {
    description: 'View a tenant',
    resourceTypes: [ResourceType.Tenant],
  },
  'admin:tenant:create': {
    description: 'Create a tenant',
    resourceTypes: [],
  },
  'admin:tenant:update': {
    description: "Edit a tenant's general information",
    resourceTypes: [ResourceType.Tenant],
  },
  'admin:tenant:delete': {
    description: 'Delete a tenant',
    resourceTypes: [ResourceType.Tenant],
  },
  'admin:zone:read': {
    description: 'View a zone',
    resourceTypes: [ResourceType.Zone],
  },
  'admin:zone:create': {
    description: 'Create a zone in a tenant',
    resourceTypes: [ResourceType.Tenant],
  },
  'admin:zone:update': {
    description: "Edit a zone's general information",
    resourceTypes: [ResourceType.Zone],
  },
  'admin:zone:delete': {
    description: 'Delete a zone',
    resourceTypes: [ResourceType.Zone],
  },
  'admin:app:read': {
    description: 'View an app',
    resourceTypes: [ResourceType.App],
  },
  'admin:app:create': {
    description: 'Create an app in a zone',
    resourceTypes: [ResourceType.Zone],
  },
  'admin:app:update': {
    description: "Edit an app's general information",
    resourceTypes: [ResourceType.App],
  },
  'admin:app:delete': {
    description: 'Delete an app',
    resourceTypes: [ResourceType.App],
  },
  'admin:scenario:read': {
    description: 'View a scenario',
    resourceTypes: [ResourceType.Scenario],
  },
  'admin:scenario:create': {
    description: 'Create a scenario in an app',
    resourceTypes: [ResourceType.App],
  },
  'admin:scenario:update': {
    description: 'Edit a scenario',
    resourceTypes: [ResourceType.Scenario],
  },
  'admin:scenario:delete': {
    description: 'Delete a scenario',
    resourceTypes: [ResourceType.Scenario],
  },
  'admin:user:read': {
    description: 'View a user',
    resourceTypes: [ResourceType.User],
  },
  'admin:user:create': {
    description: 'Create a user in a tenant',
    resourceTypes: [ResourceType.Tenant],
  },
  'admin:user:update': {
    description: "Edit a user's profile",
    resourceTypes: [ResourceType.User],
  },
  'admin:user:delete': {
    description: 'Delete a user',
    resourceTypes: [ResourceType.User],
  },
  'admin:page:create': {
    description: 'Add a page to an app',
    resourceTypes: [ResourceType.App],
  },
  'admin:page:update': {
    description: "Edit a page's settings",
    resourceTypes: [ResourceType.Page],
  },
  'admin:page:delete': {
    description: 'Remove a page from an app',
    resourceTypes: [ResourceType.Page],
  },
  'admin:app:configure': {
    description: "Read and change an app's functionality configuration",
    resourceTypes: [ResourceType.App],
  },
} as const satisfies Record<string, Action.Definition>
